/*
 * Kit Finder — preset filter for Full Delete Kits collections (snippets/kit-finder.liquid).
 *
 * The grid itself is filtered by Shopify (filter.p.m.custom.* params): we write the
 * choice into the form's hidden inputs and call CollectionFiltersForm.renderPage()
 * from 2.0-filters.js, which re-renders the section over AJAX and pushes the URL.
 *
 * To grey out tiles/options that would leave no kits (and to update "Show N kits"
 * instantly) we load fitment data for the whole, unfiltered collection once from
 * sections/kit-finder-data.liquid and count locally.
 */
(() => {
  if (customElements.get('kit-finder')) return;

  const store = (window.__kitFinder = window.__kitFinder || { data: {}, focus: null });

  // Focus is restored after the AJAX re-render only for keyboard users: on phones a focused
  // <select> reopens its native picker.
  let usingKeyboard = false;
  document.addEventListener('keydown', () => (usingKeyboard = true), true);
  document.addEventListener('pointerdown', () => (usingKeyboard = false), true);

  // Shopify filter values (value.value) arrive HTML-escaped, e.g. "Cab &amp; Chassis".
  const decoder = document.createElement('textarea');
  const decode = (value) => {
    decoder.innerHTML = value;
    return decoder.value;
  };

  // Compare values case-insensitively: the filter index can lag behind metafield edits
  // (e.g. "Mini Maxx v1" in the filter while products already say "Mini Maxx V1").
  const key = (value) => String(value).trim().toLowerCase();

  const toList = (value) => {
    if (value == null || value === '') return [];
    return (Array.isArray(value) ? value : [value]).map(key);
  };

  const scrollToGrid = () => {
    const target =
      document.querySelector('[data-product-container]') ||
      document.querySelector('.sf__collection-toolbar');
    if (!target) return;
    const header = document.querySelector('.sf-header__sticky, [data-sticky-header], .sf-header');
    const offset = (header && header.getBoundingClientRect().height) || 80;
    const top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const loadKits = (engineField) => {
    const path = window.location.pathname;
    if (store.data[path]) return store.data[path];

    const fetchPage = (page) =>
      fetch(`${path}?section_id=kit-finder-data${page > 1 ? `&page=${page}` : ''}`)
        .then((res) => {
          if (!res.ok) throw new Error('Kit Finder: failed to load data');
          return res.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return JSON.parse(doc.querySelector('[data-kit-finder-data]').textContent);
        });

    store.data[path] = fetchPage(1)
      .then(async (first) => {
        const pages = [first];
        for (let page = 2; page <= first.pages; page++) pages.push(await fetchPage(page));
        return pages.flatMap((p) =>
          Object.values(p.kits).map((kit) => ({
            engine: toList(engineField ? kit[engineField] : null),
            year: toList(kit.year),
            body: toList(kit.body),
            tuner: toList(kit.tuner),
          }))
        );
      })
      .catch((err) => {
        delete store.data[path];
        throw err;
      });

    return store.data[path];
  };

  class KitFinder extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-kf-config]').textContent);
      ['years', 'bodies', 'tuners'].forEach((key) => {
        this.config[key] = this.config[key].map(decode);
      });
      const { state } = this.config;
      this.state = { tile: state.tile, year: decode(state.year), body: decode(state.body), tuner: decode(state.tuner) };
      this.kits = null;
      this.form = this.closest('form');
      this.filtersForm = this.closest('collection-filters-form');
      this.inputs = this.form.querySelector('[data-kf-inputs]');
      this.dropdowns = {};
      this.querySelectorAll('[data-kf-dd]').forEach((dd) => {
        this.dropdowns[dd.dataset.kfDd] = dd;
      });

      // Our controls must not trigger the form's own debounced 'input' submit.
      this.addEventListener('input', (e) => e.stopPropagation());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeAll(true);
      });
      this.onOutside = (e) => {
        if (!e.target.closest('[data-kf-dd]')) this.closeAll();
      };
      document.addEventListener('pointerdown', this.onOutside);
      this.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-kf-tile]');
        if (tile) return this.onTile(Number(tile.dataset.kfTile));
        const opt = e.target.closest('[data-kf-opt]');
        if (opt) return this.onOption(opt.closest('[data-kf-dd]').dataset.kfDd, opt.dataset.kfOpt);
        const btn = e.target.closest('.kf__dd-btn');
        if (btn) return this.toggle(btn.closest('[data-kf-dd]'));
        if (e.target.closest('[data-kf-reset]')) return this.onReset();
        if (e.target.closest('[data-kf-show]')) return this.onShow();
      });

      this.restoreFocus();
      this.restoreTilesScroll();

      loadKits(this.config.engineField)
        .then((kits) => {
          if (!this.isConnected) return;
          this.kits = kits;
          this.render();
        })
        .catch(console.error);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onOutside);
    }

    // ---------- matching

    tileYears(tile) {
      const from = Math.floor(Number(tile.from));
      const to = Number(tile.to);
      return this.config.years.filter((y) => Number(y) >= from && Number(y) <= to);
    }

    yearsFor(state) {
      if (state.year) return [state.year];
      const tile = this.config.tiles[state.tile];
      return tile ? this.tileYears(tile) : null;
    }

    kitFits(kit, state) {
      const tile = this.config.tiles[state.tile];
      if (tile && tile.engines.length && !kit.engine.some((e) => tile.engines.some((t) => key(t) === e))) return false;
      const years = this.yearsFor(state);
      if (years && !kit.year.some((y) => years.some((v) => key(v) === y))) return false;
      if (state.body && !kit.body.includes(key(state.body))) return false;
      if (state.tuner && !kit.tuner.includes(key(state.tuner))) return false;
      return true;
    }

    count(state) {
      if (!this.kits) return null;
      return this.kits.reduce((n, kit) => n + (this.kitFits(kit, state) ? 1 : 0), 0);
    }

    // ---------- UI

    render() {
      const { state } = this;

      this.querySelectorAll('[data-kf-tile]').forEach((button) => {
        const index = Number(button.dataset.kfTile);
        const selected = index === state.tile;
        // Picking a tile resets the dropdowns, so a tile is grey only if it has no kits at all.
        const empty = this.count({ tile: index, year: '', body: '', tuner: '' }) === 0;
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-dimmed', empty && !selected);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = empty && !selected;
      });

      if (this.dropdowns.year) {
        const tile = this.config.tiles[state.tile];
        const years = (tile ? this.tileYears(tile) : this.config.years).slice().reverse();
        this.fillDropdown('year', 'Any year', years);
      }
      if (this.dropdowns.body) this.fillDropdown('body', 'Any body', this.config.bodies);
      if (this.dropdowns.tuner) this.fillDropdown('tuner', 'Any tuner', this.config.tuners);

      const n = this.count(state) ?? this.config.count;
      this.querySelector('[data-kf-show-label]').textContent = `Show ${n} kit${n === 1 ? '' : 's'}`;
    }

    // Custom dropdown: "Pickup (10)" = kits you'd get if you picked it; "(0)" items are greyed out.
    fillDropdown(name, anyLabel, values) {
      const dd = this.dropdowns[name];
      const current = this.state[name];
      const label = (value) => {
        const n = this.count({ ...this.state, [name]: value });
        return n == null ? value : `${value} (${n})`;
      };

      const items = [{ value: '', text: anyLabel }, ...values.map((value) => ({ value, text: label(value) }))].map(
        ({ value, text }) => {
          const isCurrent = value === current;
          const empty = value !== '' && !isCurrent && this.count({ ...this.state, [name]: value }) === 0;
          const li = document.createElement('li');
          li.setAttribute('role', 'presentation');
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', String(isCurrent));
          button.dataset.kfOpt = value;
          button.className = `kf__dd-opt${isCurrent ? ' is-current' : ''}${empty ? ' is-empty' : ''}`;
          button.disabled = empty;
          button.textContent = text;
          li.append(button);
          return li;
        }
      );
      dd.querySelector('.kf__dd-list').replaceChildren(...items);
      dd.querySelector('.kf__dd-text').textContent = current ? label(current) : anyLabel;
      dd.classList.toggle('is-set', Boolean(current));
    }

    toggle(dd) {
      const list = dd.querySelector('.kf__dd-list');
      const open = list.hidden;
      this.closeAll();
      if (!open) return;
      list.hidden = false;
      dd.querySelector('.kf__dd-btn').setAttribute('aria-expanded', 'true');
    }

    closeAll(refocus) {
      Object.values(this.dropdowns).forEach((dd) => {
        const list = dd.querySelector('.kf__dd-list');
        const btn = dd.querySelector('.kf__dd-btn');
        if (!list.hidden && refocus) btn.focus();
        list.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
      });
    }

    // ---------- actions

    onTile(index) {
      // A tile is "a new truck": start over, dropdowns are reset (also when the tile is unselected).
      this.state = { tile: this.state.tile === index ? -1 : index, year: '', body: '', tuner: '' };
      this.apply(`tile-${index}`);
    }

    onOption(key, value) {
      this.closeAll();
      this.state[key] = value;
      this.apply(key);
    }

    onReset() {
      this.state = { tile: -1, year: '', body: '', tuner: '' };
      this.apply('reset');
    }

    onShow() {
      // While the grid is re-rendering, 2.0-filters scrolls to the top of the section
      // when done — remember to go to the cards instead.
      if (store.pending) store.showAfterRender = true;
      scrollToGrid();
    }

    apply(focusId) {
      this.render();
      this.writeInputs();
      store.focus = focusId;
      store.tilesScroll = this.querySelector('.kf__tiles')?.scrollLeft ?? null;
      store.pending = true;

      const form = this.filtersForm;
      // renderPage() calls loading.finish(this.scrollToTop) after the section is replaced.
      form.scrollToTop = () => {
        store.pending = false;
        if (store.showAfterRender) {
          store.showAfterRender = false;
          scrollToGrid();
        }
        // Otherwise stay put: the page only scrolls to the cards on "Show N kits".
      };

      // The loading overlay covers #CollectionProductGrid; start it below the finder
      // so "Show N kits" stays clickable while the grid reloads.
      const grid = this.closest('#CollectionProductGrid');
      if (grid) {
        const top = this.getBoundingClientRect().bottom - grid.getBoundingClientRect().top;
        grid.style.setProperty('--kf-overlay-top', `${Math.round(top)}px`);
      }

      const params = new URLSearchParams(new FormData(this.form)).toString();
      form.renderPage(params);
    }

    writeInputs() {
      const { params, tiles } = this.config;
      const tile = tiles[this.state.tile];
      const fields = [];
      if (tile && params.engine) tile.engines.forEach((e) => fields.push([params.engine, e]));
      (this.yearsFor(this.state) || []).forEach((y) => fields.push([params.year, y]));
      if (this.state.body) fields.push([params.body, this.state.body]);
      if (this.state.tuner) fields.push([params.tuner, this.state.tuner]);

      this.inputs.replaceChildren(
        ...fields.map(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          return input;
        })
      );
    }

    // The tiles row scrolls sideways on phones; the re-render would send it back to the start.
    restoreTilesScroll() {
      const left = store.tilesScroll;
      store.tilesScroll = null;
      const tiles = this.querySelector('.kf__tiles');
      if (left == null || !tiles) return;
      const previous = tiles.style.scrollSnapType;
      tiles.style.scrollSnapType = 'none';
      tiles.scrollLeft = left;
      requestAnimationFrame(() => (tiles.style.scrollSnapType = previous));
    }

    // The section is re-rendered after every change; keep keyboard focus where it was.
    restoreFocus() {
      if (!store.focus) return;
      const target = this.querySelector(`[data-kf-id="${store.focus}"]`);
      store.focus = null;
      if (usingKeyboard) target?.focus({ preventScroll: true });
    }
  }

  customElements.define('kit-finder', KitFinder);
})();
